/**
 * CallPageAskBox — the question box at the top of a call page (sop-ee-calls-runtime
 * §4f; worker rules in worker/src/call-page-ask-core.ts).
 *
 * Renders nothing unless the Engine says so: the call must be switched to
 * 'preview' (only the preview list, on their personal link) or 'on' (members),
 * have a "question" ask from Command Centre, and have an upcoming date. A send
 * is an ordinary Call Asks "question" for that date — the host sees it in the
 * morning-of lineup and a copy lands in Slack.
 */

import { useEffect, useState, type FormEvent, type CSSProperties } from 'react';
import { suggestTypoCorrection } from '../lib/email-validator';

const API_BASE = (import.meta as unknown as { env: Record<string, string> }).env?.VITE_API_BASE_URL ?? '';

interface BoxState {
  show: boolean;
  preview?: boolean;
  prompt?: string;
  ctaLabel?: string;
  helperText?: string | null;
  closed?: boolean;
  occurrence?: { start_utc: string; timezone: string };
  knownMember?: boolean;
  firstName?: string | null;
}

function dateLabel(startUtc: string, tz: string): string {
  try {
    return new Intl.DateTimeFormat('en-US', { timeZone: tz || 'America/Toronto', weekday: 'long', month: 'long', day: 'numeric' })
      .format(new Date(startUtc));
  } catch {
    return '';
  }
}

export default function CallPageAskBox({ callSlug, at }: { callSlug: string; at: string | null }) {
  const [box, setBox] = useState<BoxState | null>(null);
  const [text, setText] = useState('');
  const [email, setEmail] = useState('');
  const [needEmail, setNeedEmail] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    const q = at ? `?at=${encodeURIComponent(at)}` : '';
    fetch(`${API_BASE}/api/public/calls/${encodeURIComponent(callSlug)}/page-ask${q}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d: BoxState | null) => {
        setBox(d && d.show ? d : null);
        if (d?.show && !d.knownMember) setNeedEmail(true);
      })
      .catch(() => setBox(null)); // the box is a nicety — never an error on the page
  }, [callSlug, at]);

  // Arriving from the Monday email's "#ask" link: bring the box into view once it exists.
  useEffect(() => {
    if (box?.show && window.location.hash === '#ask') {
      document.getElementById('ask')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [box]);

  if (!box?.show) return null;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setError(null);
    if (!text.trim()) { setError('Say a little about it before sending.'); return; }
    const typed = email.trim().toLowerCase();
    if (needEmail && !typed.includes('@')) { setError('What’s the email your membership is under?'); return; }
    setBusy(true);
    try {
      const r = await fetch(`${API_BASE}/api/public/calls/${encodeURIComponent(callSlug)}/page-ask`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: text.trim(), ...(at ? { at } : {}), ...(needEmail ? { email: typed } : {}) }),
      });
      const d = await r.json().catch(() => ({})) as { ok?: boolean; error?: string; code?: string };
      if (d.ok) { setSent(true); return; }
      if (d.code === 'need_email') setNeedEmail(true);
      setError(d.error ?? 'That didn’t send — please try again.');
    } catch {
      setError('That didn’t send — please try again.');
    } finally {
      setBusy(false);
    }
  };

  const when = box.occurrence ? dateLabel(box.occurrence.start_utc, box.occurrence.timezone) : '';
  const card: CSSProperties = {
    background: '#fff', border: '1px solid #e8e3de', borderLeft: '4px solid #F05A28', borderRadius: 16,
    padding: '28px 26px', marginBottom: 24, boxShadow: '0 2px 12px rgba(10,10,46,0.06)', scrollMarginTop: 24,
  };
  const field: CSSProperties = {
    width: '100%', boxSizing: 'border-box', padding: '12px 14px', borderRadius: 8,
    border: '1px solid #d6d0ca', fontSize: 15, fontFamily: "'Poppins', sans-serif", color: '#0A0A2E', background: '#fff',
  };
  const suggestion = needEmail ? suggestTypoCorrection(email) : null;

  return (
    <div id="ask" style={card}>
      {box.preview && (
        <div style={{
          display: 'inline-block', background: '#FEF3C7', color: '#78350F', border: '1px solid #FCD34D',
          fontSize: 12, fontWeight: 600, borderRadius: 999, padding: '3px 12px', marginBottom: 12,
        }}>
          Preview — only you can see this box until it’s switched on for members
        </div>
      )}
      <h2 style={{ fontFamily: "'Poppins', sans-serif", fontSize: 22, fontWeight: 700, margin: '0 0 6px', color: '#0A0A2E' }}>
        {box.prompt || 'Got a question or a topic for this call?'}
      </h2>
      {when && (
        <div style={{ fontSize: 14, color: '#6b7280', marginBottom: 14 }}>For the call on {when}</div>
      )}
      {sent ? (
        <div style={{ fontSize: 15, color: '#166534', background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 10, padding: '14px 16px' }}>
          Thanks{box.firstName ? `, ${box.firstName}` : ''} — it’s with the host for the call.{' '}
          <button type="button" onClick={() => { setSent(false); setText(''); }}
            style={{ background: 'none', border: 'none', color: '#F05A28', cursor: 'pointer', textDecoration: 'underline', padding: 0, fontSize: 15 }}>
            Send another
          </button>
        </div>
      ) : box.closed ? (
        <div style={{ fontSize: 15, color: '#4b5563' }}>Questions for this date are closed — try again for the next one.</div>
      ) : (
        <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <textarea value={text} onChange={(e) => setText(e.target.value)} maxLength={500} rows={4}
            placeholder="Your question or topic" style={{ ...field, resize: 'vertical' }} />
          {needEmail && (
            <>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email"
                placeholder="The email your membership is under" style={field} />
              {suggestion && (
                <button type="button" onClick={() => setEmail(suggestion)}
                  style={{ background: 'none', border: 'none', color: '#F05A28', cursor: 'pointer', textAlign: 'left', padding: 0, fontSize: 13 }}>
                  Did you mean {suggestion}?
                </button>
              )}
            </>
          )}
          {error && <div style={{ fontSize: 13, color: '#b91c1c' }}>{error}</div>}
          <div>
            <button type="submit" disabled={busy} className="cta-btn" style={{
              background: '#F05A28', color: '#fff', border: 'none', cursor: busy ? 'wait' : 'pointer',
              padding: '12px 24px', borderRadius: 8, fontSize: 15, fontWeight: 700, opacity: busy ? 0.7 : 1,
              fontFamily: "'Poppins', sans-serif",
            }}>
              {busy ? 'Sending…' : (box.ctaLabel || 'Send it in')}
            </button>
          </div>
          {box.helperText && <div style={{ fontSize: 13, color: '#6b7280', lineHeight: 1.5 }}>{box.helperText}</div>}
        </form>
      )}
    </div>
  );
}
